import React, { useState, useEffect, useContext } from "react";
import { Container, Card, CardBody } from "reactstrap";
import DataTable from "react-data-table-component";
import { getStockByProccesingPlantId } from "../../../api/stock/getStockByProccesingPlantId";
import { updateProccesingPlantStatus } from "../../../api/stock/proccesingPlantStatusChange";
import { getIdGivenUserId } from '../../../api/processing plant/plantApi';
import { getTruckByPlantId } from '../../../api/TruckDriver/TruckDriver';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { AuthContext } from '../../ExtraPages/AuthProvider';
import { useNavigate } from 'react-router-dom';

const ProcessingPlantStockAccept = () => {
  const [stockData, setStockData] = useState([]);
  const [plantId, setPlantId] = useState(null);
  const [truckDrivers, setTruckDrivers] = useState([]);
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch plantId based on user.uid
  useEffect(() => {
    const fetchPlantId = async () => {
      try {
        if (user?.uid) {
          const response = await getIdGivenUserId(user.uid);
          console.log('Plant ID response:', response.data);
          setPlantId(response.data);
        }
      } catch (error) {
        console.error('Error fetching plant ID:', error);
        Swal.fire('Error!', 'Failed to fetch plant ID.', 'error');
      }
    };

    fetchPlantId();
  }, [user]);

  // Fetch stock data and truck drivers when plantId is available
  useEffect(() => {
    if (!plantId) return;

    const fetchPlantsStockData = async () => {
      try {
        console.log('Fetching stock data for plantId:', plantId);
        const response = await getStockByProccesingPlantId(user.uid);
        console.log('Stock data response:', response);

        if (Array.isArray(response.data.data)) {
          setStockData(response.data.data);
        } else {
          console.error('Unexpected stock data format:', response.data);
          setStockData([]);
        }
      } catch (error) {
        console.error('Error fetching stock data:', error.message);
        setStockData([]);
      }
    };

    const fetchTruckDrivers = async () => {
      try {
        console.log('Fetching truck drivers for plantId:', plantId);
        const response = await getTruckByPlantId(plantId);
        console.log('Truck drivers response:', response);

        if (Array.isArray(response.data.data)) {
          setTruckDrivers(response.data.data);
        } else {
          console.error('Unexpected truck drivers format:', response.data);
          setTruckDrivers([]);
        }
      } catch (error) {
        console.error('Error fetching truck drivers:', error.message);
        setTruckDrivers([]);
      }
    };

    fetchPlantsStockData();
    fetchTruckDrivers();
  }, [plantId, user.uid]);

  const handleAccept = async (id) => {
    Swal.fire({
      title: 'Set Estimated Processing Time & Assign Truck Driver',
      html: `
        <div style="display: flex; flex-direction: column; gap: 15px;">
          <div style="display: flex; justify-content: center; align-items: center; gap: 10px;">
            <input type="number" id="days" class="swal2-input" placeholder="Days" min="1" 
              style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
            <input type="number" id="hours" class="swal2-input" placeholder="Hours" min="0" max="23" 
              style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
          </div>
          <select id="truckDriver" class="swal2-select" style="width: 80%; height: 35px; font-size: 14px;">
            <option value="" disabled selected>Select Truck Driver</option>
            ${truckDrivers
              .map(
                (driver) =>
                  `<option value="${driver._id}">${
                    driver.firstName || 'Unnamed'
                  } ${driver.lastName || 'Driver'}</option>`
              )
              .join('')}
          </select>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Set & Accept',
      preConfirm: () => {
        const days = parseInt(document.getElementById('days').value);
        const hours = parseInt(document.getElementById('hours').value) || 0;
        const truckDriverId = document.getElementById('truckDriver').value;

        if (!truckDriverId) {
          Swal.showValidationMessage('Please select a truck driver');
          return false;
        }
        if (!days || days < 1) {
          Swal.showValidationMessage('Days must be at least 1');
          return false;
        }

        return {
          totalHours: days * 24 + hours,
          truckDriverId,
        };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { totalHours, truckDriverId } = result.value;
        const data = {
          id,
          status: true,
          EPT: totalHours,
          truckDriverId,
        };

        try {
          const response = await updateProccesingPlantStatus(data);
          console.log('Form submitted successfully:', response.data);
          Swal.fire(
            'Accepted!',
            'The stock has been accepted and truck driver assigned.',
            'success'
          );

          const responseStock = await getStockByProccesingPlantId(user.uid);
          if (Array.isArray(responseStock.data.data)) {
            setStockData(responseStock.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  const handleReject = async (id) => {
    const data = {
      id,
      status: false,
    };

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reject it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await updateProccesingPlantStatus(data);
          console.log('Form submitted successfully:', response.data);
          Swal.fire('Rejected!', 'The stock has been rejected.', 'success');

          const responseStock = await getStockByProccesingPlantId(user.uid);
          if (Array.isArray(responseStock.data.data)) {
            setStockData(responseStock.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  const handleTracking = (truckDriverId) => {
    if (truckDriverId) {
      navigate(`/truckTracking/${truckDriverId}`);
    } else {
      Swal.fire('Error!', 'No truck driver assigned.', 'error');
    }
  };

  const columns = [
    { name: '#', selector: (row, index) => index + 1, sortable: true },
    { name: 'Fish Type', selector: (row) => row.Product_Name, sortable: true },
    { name: 'Vessel Name', selector: (row) => row.Boat_Number, sortable: true },
    { name: 'Harbour', selector: (row) => row.Harbour, sortable: true },
    {
      name: 'Quality Grade',
      selector: (row) => row.Quality_Grade,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => row.Processing_Plant_status,
      sortable: true,
      center: true, // ✅ Centers cell content
      cell: (row) => {
        if (row.Processing_Plant_status === 'Pending') {
          return <span className="status-button-warning">Pending</span>;
        } else if (row.Processing_Plant_status === 'true') {
          return <span className="status-button-success">Accepted</span>;
        } else if (row.Processing_Plant_status === 'false') {
          return <span className="status-button-danger">Rejected</span>;
        }
        return <span>{row.Processing_Plant_status}</span>;
      },
    },
    {
      name: 'Actions',
      cell: (row) => {
        if (row.Processing_Plant_status === 'Pending') {
          return (
            <>
              <button
                className="table-button table-button-success"
                style={{ marginRight: '4px' }}
                size="sm"
                onClick={() => handleAccept(row._id)}
              >
                Accept
              </button>
              <button
                className="table-button table-button-danger"
                size="sm"
                onClick={() => handleReject(row._id)}
              >
                Reject
              </button>
            </>
          );
        } else if (row.Processing_Plant_status === 'true') {
          return (
            <>
              {row.truckDriverId && (
                <button
                  className="table-button table-button-info"
                  style={{ marginLeft: '8px' }}
                  size="sm"
                  onClick={() => handleTracking(row.truckDriverId)}
                >
                  Tracking
                </button>
              )}
            </>
          );
        } else if (row.Processing_Plant_status === 'false') {
          return <span className="status-button-danger">Rejected</span>;
        }
        return null;
      },
    },
  ];

  if (loading) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Loading...</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Please log in to view processing approvals.</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ minHeight: '100%' }}>
        <Container fluid>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ backgroundColor: '#fff' }}>
              <CardBody style={{ padding: '20px' }}>
                <h5 className="mb-4">Processing Approval</h5>
                <DataTable
                  columns={columns}
                  data={stockData}
                  pagination
                />
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
    </motion.div>
  );
};

export default ProcessingPlantStockAccept;
